'use client';

import React, { useState, useEffect } from 'react';
import { Lock, Play, Clock, Users, ChevronRight, Timer, Sparkles } from 'lucide-react';
import type { Game } from './GamesSection';

interface GameCardProps {
  game: Game;
  onClick: () => void;
  isPremium?: boolean;
  trialEndDate?: string | null;
}

function useTrialTimeLeft(endDate: string | null | undefined) {
  const [label, setLabel] = useState<string | null>(null);

  useEffect(() => {
    if (!endDate) return;

    const update = () => {
      const diff = new Date(endDate).getTime() - Date.now();
      if (diff <= 0) { setLabel(null); return; }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      if (hours > 0) {
        setLabel(`${hours}h ${minutes}min verbleibend`);
      } else {
        setLabel(`${minutes}min verbleibend`);
      }
    };

    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, [endDate]);

  return label;
}

export default function GameCard({ game, onClick, isPremium = false, trialEndDate }: GameCardProps) {
  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);

  const isLocked = game.premium && !isPremium;
  const trialLabel = useTrialTimeLeft(isLocked ? trialEndDate : null);

  return (
    <div
      className={`relative rounded-2xl overflow-hidden cursor-pointer group ${isLocked ? 'game-card-locked' : ''}`}
      style={{
        background: 'var(--card)',
        border: `1px solid ${hovered && !isLocked ? game.color + '70' : 'var(--border)'}`,
        transform: pressed
          ? 'translateY(-2px) scale(0.98)'
          : hovered
          ? 'translateY(-8px) scale(1.025)'
          : 'translateY(0) scale(1)',
        boxShadow: hovered
          ? `0 24px 64px ${game.color}35, 0 0 0 1px ${game.color}20`
          : '0 2px 8px rgba(0,0,0,0.2)',
        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s ease, border-color 0.2s ease',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      aria-label={`${game.name} spielen${isLocked ? ' (Premium)' : ''}`}
    >
      {/* Animated color accent bar */}
      <div
        className="h-1 w-full"
        style={{
          background: `linear-gradient(90deg, ${game.color}, ${game.color}80, transparent)`,
          backgroundSize: hovered ? '200% 100%' : '100% 100%',
          opacity: hovered ? 1 : 0.5,
          transition: 'opacity 0.3s ease, background-size 0.5s ease',
        }}
      />

      {/* Hover glow overlay */}
      {hovered && !isLocked && (
        <div
          className="absolute inset-0 pointer-events-none opacity-5 rounded-2xl"
          style={{ background: `radial-gradient(circle at 50% 0%, ${game.color}, transparent 60%)` }}
        />
      )}

      {/* Premium lock badge */}
      {isLocked && (
        <div className="absolute top-3 right-3 z-10">
          <span className="premium-badge flex items-center gap-1">
            <Lock size={9} />
            PREMIUM
          </span>
        </div>
      )}

      {/* Trial countdown badge */}
      {isLocked && trialLabel && (
        <div className="absolute top-3 left-3 z-10">
          <span
            className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
            style={{
              background: 'rgba(34,211,238,0.15)',
              color: '#22D3EE',
              border: '1px solid rgba(34,211,238,0.35)',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Timer size={9} />
            {trialLabel}
          </span>
        </div>
      )}

      <div className="p-5">
        {/* Emoji — spring bounce on hover */}
        <div
          className="text-4xl mb-3"
          style={{
            transform: hovered ? 'scale(1.25) rotate(-8deg)' : 'scale(1) rotate(0deg)',
            transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
            display: 'inline-block',
          }}
        >
          {game.emoji}
        </div>

        {/* Name & Description */}
        <h4 className="font-bold text-base mb-1.5 leading-tight">{game.name}</h4>
        <p className="text-xs leading-relaxed mb-3 line-clamp-2" style={{ color: 'var(--muted-foreground)' }}>
          {game.description}
        </p>

        {/* Recommendation */}
        <div
          className="rounded-lg p-2.5 mb-3 text-xs leading-relaxed"
          style={{
            background: `${game.color}10`,
            border: `1px solid ${game.color}${hovered ? '35' : '20'}`,
            transition: 'border-color 0.2s ease',
          }}
        >
          <span className="font-semibold" style={{ color: game.color }}>💡 Tipp: </span>
          <span style={{ color: 'var(--muted-foreground)' }}>{game.recommendation}</span>
        </div>

        {/* Meta */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Users size={11} />
            {game.players}
          </div>
          <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted-foreground)' }}>
            <Clock size={11} />
            {game.duration}
          </div>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {game.tags.map((tag) => (
            <span
              key={`tag-${game.id}-${tag}`}
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{
                background: `${game.color}${hovered ? '25' : '15'}`,
                color: game.color,
                border: `1px solid ${game.color}25`,
                transition: 'background 0.2s ease',
              }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* CTA */}
        <button
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold"
          style={{
            background: isLocked
              ? 'linear-gradient(135deg, #F59E0B, #EF4444)'
              : hovered
              ? `${game.color}25`
              : 'var(--muted)',
            color: isLocked ? '#fff' : hovered ? game.color : 'var(--muted-foreground)',
            border: isLocked ? 'none' : `1px solid ${hovered ? game.color + '50' : 'var(--border)'}`,
            transition: 'all 0.2s ease',
            transform: hovered ? 'scale(1.02)' : 'scale(1)',
          }}
        >
          {isLocked ? (
            <>
              <Sparkles size={13} />
              Premium freischalten
            </>
          ) : (
            <>
              <Play size={13} />
              Jetzt spielen
              <ChevronRight
                size={13}
                style={{
                  transform: hovered ? 'translateX(3px)' : 'translateX(0)',
                  transition: 'transform 0.2s ease',
                }}
              />
            </>
          )}
        </button>
      </div>
    </div>
  );
}